<script>
import helpers from '@/lib/helpers'
import Files from '@/components/files/files.vue'

/**
 * The file store (the user menu's File Store): folders of files kept to
 * reuse - a spec sheet, a terms document, a form - attached to emails from
 * the composer's Attachments tab. System folders are everyone's; a personal
 * folder is yours alone. Folders down the left, the chosen folder's files on
 * the right, through the standard files block (upload, URL, delete).
 */
export default {
  name: 'file-store-modal',
  components: {Files},

  data() {
    return {
      system: [],
      personal: [],
      canWrite: false,
      canWriteSystem: false,
      loading: false,
      // the folder open on the right
      current: null,
      // the new-folder line: null when closed, else {name, scope}
      adding: null,
      // the folder being renamed: {id, name} or null
      renaming: null,
      // narrow screens show one pane at a time: the folders, or (true) the open folder's files
      opened: false,
    }
  },

  mounted() {
    this.load()
  },

  computed: {
    /** the permission the files block checks before offering uploads for the open folder */
    writePerm() {
      return this.current && this.current.system ? 'filestore.system.write' : 'filestore.write'
    },
  },

  methods: {
    async load(keep = true) {
      this.loading = true
      try {
        const r = await this.asyncAction('/api/filestore', 'GET', {noload: true})
        this.system = r.system
        this.personal = r.personal
        this.canWrite = r.can_write
        this.canWriteSystem = r.can_write_system
        const all = [...this.system, ...this.personal]
        if (keep && this.current)
          this.current = all.find(f => f.id === this.current.id) || null
        if (!this.current && all.length)
          this.current = all[0]
      } finally {
        this.loading = false
      }
    },
    open(folder) {
      this.current = folder
      this.opened = true
    },
    openAdd(scope) {
      this.renaming = null
      this.adding = {name: '', scope}
    },
    async add() {
      if (!this.adding || !this.adding.name.trim())
        return
      const r = await this.asyncAction('/api/filestore', 'POST', {data: {name: this.adding.name.trim(), scope: this.adding.scope}})
      this.adding = null
      await this.load()
      this.current = [...this.system, ...this.personal].find(f => f.id === r.id) || this.current
    },
    openRename(folder) {
      this.adding = null
      this.renaming = {id: folder.id, name: folder.name}
    },
    async rename() {
      if (!this.renaming || !this.renaming.name.trim())
        return
      await this.asyncAction('/api/filestore/' + this.renaming.id, 'PUT', {data: {name: this.renaming.name.trim()}})
      this.renaming = null
      await this.load()
    },
    async remove(folder) {
      if (!await helpers.confirm('Delete the folder "' + folder.name + '"? Its files stay on any email they were attached to.'))
        return
      await this.asyncAction('/api/filestore/' + folder.id, 'DELETE')
      if (this.current && this.current.id === folder.id) {
        this.current = null
        this.opened = false
      }
      await this.load()
    },
  },
}
</script>

<template>
  <modal title="File Store" height="600px" size="xl" dialog-class="file-store-dialog" no-scroll>
    <!-- narrow, with a folder open: back to the folders from the title bar -->
    <template #title>
      <b-link v-if="opened" class="d-md-none text-reset mr-2" title="Folders" @click="opened = false">
        <font-awesome-icon :icon="['fas', 'arrow-left']"/>
      </b-link>
      File Store
    </template>
    <div class="d-flex h-100 file-store">
      <!-- the folders (narrow: the first pane, hidden once a folder is open) -->
      <div class="file-store-folders flex-shrink-0 overflow-auto" :class="{'d-none d-md-block': opened}">
        <template v-for="group in [{key: 'system', label: 'System', rows: system, add: canWriteSystem}, {key: 'personal', label: 'Personal', rows: personal, add: canWrite}]">
          <div :key="group.key + '-h'" class="d-flex align-items-center text-uppercase small text-muted mt-2 mb-1">
            {{ group.label }}
            <b-button v-if="group.add" size="sm" variant="link" class="p-0 ml-auto" :title="'New ' + group.label.toLowerCase() + ' folder'" @click="openAdd(group.key === 'system' ? 'S' : 'P')">
              <font-awesome-icon :icon="['fas', 'plus']"/>
            </b-button>
          </div>
          <b-list-group :key="group.key" flush class="mb-2">
            <b-list-group-item v-for="f in group.rows" :key="f.id" button :active="current && current.id === f.id" class="py-1 px-2 d-flex align-items-center" @click="open(f)">
              <template v-if="renaming && renaming.id === f.id">
                <b-form-input v-model="renaming.name" size="sm" autofocus @keydown.enter.prevent="rename" @keydown.esc="renaming = null" @click.stop/>
                <b-button size="sm" variant="link" class="p-0 ml-1" title="Save" @click.stop="rename"><font-awesome-icon :icon="['fas', 'check']"/></b-button>
              </template>
              <template v-else>
                <font-awesome-icon :icon="['fas', 'folder']" fixed-width class="mr-1 text-muted"/>
                <span class="text-truncate">{{ f.name }}</span>
                <small class="ml-auto pl-2" :class="current && current.id === f.id ? '' : 'text-muted'">{{ f.files_count }}</small>
                <b-dropdown v-if="f.can_write" size="sm" variant="link" toggle-class="p-0 ml-1 text-reset" no-caret right @click.native.stop>
                  <template #button-content><font-awesome-icon :icon="['fas', 'ellipsis']"/></template>
                  <b-dropdown-item-button @click.stop="openRename(f)">Rename</b-dropdown-item-button>
                  <b-dropdown-item-button variant="danger" @click.stop="remove(f)">Delete folder</b-dropdown-item-button>
                </b-dropdown>
              </template>
            </b-list-group-item>
            <b-list-group-item v-if="adding && adding.scope === (group.key === 'system' ? 'S' : 'P')" :key="group.key + '-add'" class="py-1 px-2 d-flex align-items-center">
              <b-form-input v-model="adding.name" size="sm" placeholder="Folder name" autofocus @keydown.enter.prevent="add" @keydown.esc="adding = null"/>
              <b-button size="sm" variant="link" class="p-0 ml-1" title="Create" @click="add"><font-awesome-icon :icon="['fas', 'check']"/></b-button>
            </b-list-group-item>
            <div v-if="!group.rows.length && !adding" :key="group.key + '-none'" class="small text-muted font-italic px-2 py-1">
              {{ group.key === 'system' ? 'No system folders yet.' : 'No personal folders yet.' }}
            </div>
          </b-list-group>
        </template>
      </div>

      <!-- the open folder's files (narrow: the second pane; the title bar's arrow goes back) -->
      <div class="flex-grow-1 min-w-0 flex-column" :class="opened ? 'd-flex' : 'd-none d-md-flex'">
        <template v-if="current">
          <div class="mb-1">
            <strong>{{ current.name }}</strong>
            <small class="text-muted ml-2">{{ current.system ? 'System folder - everyone can use these' : 'Personal folder - listed for you only' }}</small>
          </div>
          <files :key="current.id" :url="'/api/filestore/' + current.id + '/file'" :write="writePerm" standalone no-public height="500px" @refresh="load()"/>
        </template>
        <div v-else-if="!loading" class="text-muted font-italic py-5 text-center">
          Make a folder to keep files in - they can then be attached to any email from the composer's Attachments tab.
        </div>
      </div>
    </div>
  </modal>
</template>

<style>
.modal-dialog.file-store-dialog {
  max-width: 1000px;
}
</style>

<style scoped>
/* narrow: the folder list fills the modal; wide: a 260px column beside the files */
.file-store-folders {
  width: 100%;
}

@media (min-width: 768px) {
  .file-store-folders {
    width: 260px;
    border-right: 1px solid #dee2e6;
    padding-right: .5rem;
    margin-right: 1rem;
  }
}

.min-w-0 {
  min-width: 0;
}
</style>
